import { useId } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

interface BaseProps {
  label: string;
  hint?: string;
}

interface TextProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'number';
  placeholder?: string;
}

function Wrapper({ id, label, hint, children }: BaseProps & { id: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label} {hint && <span className="hint">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

export function TextField({ label, hint, value, onChange, type = 'text', placeholder }: TextProps) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint}>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      />
    </Wrapper>
  );
}

export function AreaField({ label, hint, value, onChange, placeholder }: TextProps) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint}>
      <textarea
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
      />
    </Wrapper>
  );
}

interface ListProps extends BaseProps {
  value: string[];
  onChange: (value: string[]) => void;
}

/** Listfält redigeras som en rad per post. */
export function ListField({ label, hint = '(en rad per post)', value, onChange }: ListProps) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint}>
      <textarea
        id={id}
        value={value.join('\n')}
        onChange={(event) =>
          onChange(
            event.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
          )
        }
      />
    </Wrapper>
  );
}

interface SelectProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

export function SelectField({ label, hint, value, onChange, options }: SelectProps) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint}>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function ToggleField({ label, checked, onChange }: ToggleProps) {
  return (
    <div className="field field-toggle">
      <label>
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        {label}
      </label>
    </div>
  );
}
